import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

export const useClientSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentAuctionLaneId, setCurrentAuctionLaneId] = useState(null);
  const socketRef = useRef(null);
  const connectionAttemptedRef = useRef(false);

  // Initialize socket connection
  const connect = useCallback(() => {
    if (socketRef.current?.connected || connectionAttemptedRef.current) {
      console.log(
        "Client socket: Connection already attempted or connected, skipping"
      );
      return;
    }

    connectionAttemptedRef.current = true;
    setIsConnecting(true);

    // Get auth token from localStorage
    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : null;

    if (!authUser || !token) {
      console.error("Client socket: No auth token found");
      setIsConnecting(false);
      connectionAttemptedRef.current = false;
      return;
    }

    // Get backend URL - adjust this based on your backend configuration
    const backendUrl = process.env.REACT_APP_SOCKET_URL;

    const socket = io(backendUrl, {
      auth: { token },
      transports: ["websocket", "polling"],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      forceNew: true,
    });

    socket.on("connect", () => {
      setIsConnected(true);
      setIsConnecting(false);
      connectionAttemptedRef.current = false;
    });

    socket.on("disconnect", (reason) => {
      console.log("Client socket: Disconnected from auction server:", reason);
      setIsConnected(false);
      setCurrentAuctionLaneId(null);
    });

    socket.on("connect_error", (error) => {
      console.error("Client socket: Connection error:", error);
      setIsConnecting(false);
      connectionAttemptedRef.current = false;
    });

    socketRef.current = socket;
  }, []);

  // Disconnect socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setCurrentAuctionLaneId(null);
      connectionAttemptedRef.current = false;
    }
  }, []);

  // Join auction lane
  const joinAuctionLane = useCallback((auctionLaneId) => {
    if (!socketRef.current?.connected) {
      console.error("Client socket: Socket not connected");
      return;
    }

    socketRef.current.emit("joinAuctionLane", { auctionLaneId });
    setCurrentAuctionLaneId(auctionLaneId);
  }, []);

  // Leave auction lane
  const leaveAuctionLane = useCallback(
    (auctionLaneId) => {
      if (!socketRef.current?.connected) {
        console.error("Client socket: Socket not connected");
        return;
      }

      console.log("Client socket: Leaving auction lane:", auctionLaneId);
      socketRef.current.emit("leaveAuctionLane", { auctionLaneId });
      if (currentAuctionLaneId === auctionLaneId) {
        setCurrentAuctionLaneId(null);
      }
    },
    [currentAuctionLaneId]
  );

  // Place bid
  const placeBid = useCallback((auctionLaneId, vehicleId, amount) => {
    if (!socketRef.current?.connected) {
      console.error("Client socket: Socket not connected");
      return;
    }

    socketRef.current.emit("placeBid", {
      auctionLaneId,
      vehicleId,
      amount,
    });
  }, []);

  // Event listeners
  const onNewBid = useCallback((callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on("newBid", callback);
    return () => {
      socketRef.current?.off("newBid", callback);
    };
  }, []);

  const onJoinedAuctionLane = useCallback((callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on("joinedAuctionLane", callback);
    return () => {
      socketRef.current?.off("joinedAuctionLane", callback);
    };
  }, []);

  const onLeftAuctionLane = useCallback((callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on("leftAuctionLane", callback);
    return () => {
      socketRef.current?.off("leftAuctionLane", callback);
    };
  }, []);

  const onError = useCallback((callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on("error", callback);
    return () => {
      socketRef.current?.off("error", callback);
    };
  }, []);

  const onBidConfirmation = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("bidConfirmation", callback);
    return () => {
      socketRef.current?.off("bidConfirmation", callback);
    };
  }, []);

  const onVehicleSold = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("vehicleSold", callback);
    return () => {
      socketRef.current?.off("vehicleSold", callback);
    };
  }, []);

  const onVehicleCancelled = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("vehicleCancelled", callback);
    return () => {
      socketRef.current?.off("vehicleCancelled", callback);
    };
  }, []);

  // Pulsing animation event handlers
  const onRedPulsingStarted = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("redPulsingStarted", callback);
    return () => {
      socketRef.current?.off("redPulsingStarted", callback);
    };
  }, []);

  const onGreenPulsingStarted = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("greenPulsingStarted", callback);
    return () => {
      socketRef.current?.off("greenPulsingStarted", callback);
    };
  }, []);

  const onPulsingStopped = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("pulsingStopped", callback);
    return () => {
      socketRef.current?.off("pulsingStopped", callback);
    };
  }, []);

  const onAuctionStarted = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("auctionStarted", callback);
    return () => {
      socketRef.current?.off("auctionStarted", callback);
    };
  }, []);

  const onAuctionPaused = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("auctionPaused", callback);
    return () => {
      socketRef.current?.off("auctionPaused", callback);
    };
  }, []);

  const onAuctionResumed = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("auctionResumed", callback);
    return () => {
      socketRef.current?.off("auctionResumed", callback);
    };
  }, []);

  const onAuctionEnded = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("auctionEnded", callback);
    return () => {
      socketRef.current?.off("auctionEnded", callback);
    };
  }, []);

  const onAuctionStatusChanged = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("auctionStatusChanged", callback);
    return () => {
      socketRef.current?.off("auctionStatusChanged", callback);
    };
  }, []);

  // Periodic update event handlers
  const onPeriodicUpdate = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("periodicUpdate", callback);
    return () => {
      socketRef.current?.off("periodicUpdate", callback);
    };
  }, []);

  const onPeriodicUpdatesStarted = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("periodicUpdatesStarted", callback);
    return () => {
      socketRef.current?.off("periodicUpdatesStarted", callback);
    };
  }, []);

  const onPeriodicUpdatesStopped = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("periodicUpdatesStopped", callback);
    return () => {
      socketRef.current?.off("periodicUpdatesStopped", callback);
    };
  }, []);

  // Get current timer state
  const getTimerState = useCallback((auctionLaneId) => {
    if (!socketRef.current?.connected) {
      console.error("Client socket: Socket not connected");
      return;
    }

    socketRef.current.emit("getTimerState", { auctionLaneId });
  }, []);

  // Timer state event handler
  const onTimerState = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("timerState", callback);
    return () => {
      socketRef.current?.off("timerState", callback);
    };
  }, []);

  const onBidDeleted = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("bidDeleted", callback);
    return () => {
      socketRef.current?.off("bidDeleted", callback);
    };
  }, []);

  const onBidEdited = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("bidEdited", callback);
    return () => {
      socketRef.current?.off("bidEdited", callback);
    };
  }, []);

  const onTimerReset = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("timerReset", callback);
    return () => {
      socketRef.current?.off("timerReset", callback);
    };
  }, []);

  // Listen to timer auto-reset events
  const onTimerAutoReset = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("timerAutoReset", callback);
    return () => {
      socketRef.current?.off("timerAutoReset", callback);
    };
  }, []);

  // Get complete pulsing status
  const getPulsingStatus = useCallback((auctionLaneId) => {
    if (!socketRef.current?.connected) {
      console.error("Client socket: Socket not connected");
      return;
    }

    socketRef.current.emit("getPulsingStatus", { auctionLaneId });
  }, []);

  // Listen to complete pulsing status events
  const onPulsingStatus = useCallback((callback) => {
    if (!socketRef.current) return;

    socketRef.current.on("pulsingStatus", callback);
    return () => {
      socketRef.current?.off("pulsingStatus", callback);
    };
  }, []);

  // Listen to participants list updates
  const onParticipants = useCallback((callback) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on("participants", callback);
    return () => {
      socketRef.current?.off("participants", callback);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isConnecting,
    currentAuctionLaneId,
    connect,
    disconnect,
    joinAuctionLane,
    leaveAuctionLane,
    placeBid,
    onNewBid,
    onJoinedAuctionLane,
    onLeftAuctionLane,
    onBidConfirmation,
    onBidEdited,
    onError,
    onVehicleSold,
    onVehicleCancelled,
    onRedPulsingStarted,
    onGreenPulsingStarted,
    onPulsingStopped,
    onAuctionStarted,
    onAuctionPaused,
    onAuctionResumed,
    onAuctionEnded,
    onAuctionStatusChanged,
    onPeriodicUpdate,
    onPeriodicUpdatesStarted,
    onPeriodicUpdatesStopped,
    getTimerState,
    onTimerState,
    onBidDeleted,
    onTimerReset,
    onTimerAutoReset,
    getPulsingStatus,
    onPulsingStatus,
    onParticipants,
  };
};
